import { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { toast } from "sonner";
import {
  CheckCircle2, Sparkles, CreditCard, ExternalLink, Loader2,
  Receipt, ShieldCheck, AlertCircle, Clock,
} from "lucide-react";

/**
 * Billing page for Company Admins. Shows current plan, billing status, a plan
 * picker, "Manage Billing" portal launch, and the last 5 invoices.
 *
 * Lifecycle:
 *  - On mount: fetch /billing/me, /billing/plans, /billing/invoices, /founding/status.
 *  - Detects ?checkout=success in URL (Stripe redirect) → toast + refetch.
 *  - Detects ?checkout=cancelled → toast info.
 *
 * Notes:
 *  - Employees never reach this page (route is gated by company_admin role).
 *  - Buttons render disabled while requests are in flight to prevent double-charge.
 */
const STATUS_COPY = {
  trial:           { tone: "info",    label: "Trial",            icon: Clock },
  active:          { tone: "success", label: "Active",           icon: ShieldCheck },
  payment_issue:   { tone: "warning", label: "Payment issue",    icon: AlertCircle },
  cancelled:       { tone: "danger",  label: "Cancelled",        icon: AlertCircle },
  incomplete:      { tone: "warning", label: "Incomplete",       icon: AlertCircle },
  expired:         { tone: "danger",  label: "Expired",          icon: AlertCircle },
};

const TONE_PILL = {
  info:    "bg-[#DBE7FE] text-[#1D4ED8]",
  success: "bg-[#DCFCE7] text-[#15803D]",
  warning: "bg-[#FEF3C7] text-[#92400E]",
  danger:  "bg-[#FEE2E2] text-[#991B1B]",
};

function gbp(amount) {
  if (amount == null) return "£-";
  return `£${Number(amount).toFixed(2)}`;
}

function formatDate(iso) {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  } catch { return iso; }
}

export default function BillingPage() {
  const [me, setMe] = useState(null);
  const [plans, setPlans] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [founding, setFounding] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);  // slug being checked out, or "portal"
  const [searchParams] = useSearchParams();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [meR, plansR, invR, fR] = await Promise.all([
        api.get("/billing/me"),
        api.get("/billing/plans"),
        api.get("/billing/invoices"),
        api.get("/founding/status"),
      ]);
      setMe(meR.data);
      setPlans(plansR.data.plans || []);
      setInvoices(invR.data.invoices || []);
      setFounding(fR.data);
    } catch (e) {
      toast.error(formatError(e?.response?.data?.detail) || "Failed to load billing");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Stripe Checkout return handler. URL is the source of truth; we use a ref
  // to ensure we only react to a given URL token once. We replace the URL via
  // history.replaceState (not setSearchParams) so the effect doesn't reschedule.
  const handledTokenRef = useRef(null);
  useEffect(() => {
    const ck = searchParams.get("checkout");
    if (!ck || handledTokenRef.current === ck) return;
    handledTokenRef.current = ck;
    if (ck === "success") {
      toast.success("Subscription updated. Welcome aboard!");
      load();
      const t = setTimeout(load, 3000);
      window.history.replaceState({}, "", window.location.pathname);
      return () => clearTimeout(t);
    }
    if (ck === "cancelled") {
      toast.info("Checkout cancelled. Your plan is unchanged.");
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, [searchParams, load]);

  const startCheckout = async (slug) => {
    setBusy(slug);
    try {
      const r = await api.post("/billing/checkout", {
        plan_slug: slug,
        origin_url: window.location.origin,
      });
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(formatError(e?.response?.data?.detail) || "Could not start checkout");
      setBusy(null);
    }
  };

  const openPortal = async () => {
    setBusy("portal");
    try {
      const r = await api.post("/billing/portal", { origin_url: window.location.origin });
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(formatError(e?.response?.data?.detail) || "Could not open billing portal");
      setBusy(null);
    }
  };

  if (loading || !me) {
    return (
      <div className="flex items-center justify-center py-16" data-testid="billing-loading">
        <Loader2 className="h-6 w-6 animate-spin text-[#2563EB]" />
      </div>
    );
  }

  const isFounding = me.membership_type === "founding_customer";
  const status = STATUS_COPY[me.billing_status] || (isFounding
    ? { tone: "info", label: "Founding Customer", icon: Sparkles }
    : { tone: "info", label: "Trial", icon: Clock });
  const StatusIcon = status.icon;
  const currentPlan = plans.find(p => p.slug === me.plan_slug) || (isFounding ? plans.find(p => p.slug === "founding") : null);

  // R5.a (Feb 2026) - Active trial detection uses the same signal as
  // compute_access_state on the server. Only pure trial customers see
  // the "Change trial size" section: paid customers, founding customers
  // and expired trials are excluded.
  const isActiveTrial = (
    (me.access?.reason === "trial")
    && (me.access?.access === "full")
    && !me.stripe_subscription_id
    && !isFounding
  );

  return (
    <div className="space-y-8" data-testid="billing-page">
      {/* Header */}
      <div>
        <div className="text-xs uppercase tracking-widest text-[#4B5773]">Billing</div>
        <h1 className="font-display text-3xl sm:text-4xl text-[#0B1A36] mt-1">Plan &amp; payments</h1>
        <p className="text-sm text-[#4B5773] mt-2 max-w-2xl">
          Manage your subscription, payment method, invoices and plan changes from here. Test mode is active &mdash; no real charges.
        </p>
      </div>

      {/* R5.a (Feb 2026) - Change trial size section. Trial employee
          limit is controlled by plan_slug; feature access remains
          full Command for the 14-day trial. Visible only for active
          trial tenants. */}
      {isActiveTrial && (
        <TrialSizeSection me={me} onChanged={load} />
      )}

      {/* Status + current plan */}
      <div className="grid md:grid-cols-3 gap-4">
        {/* Plan */}
        <div className="md:col-span-2 rounded-xl border border-slate-200 bg-white p-6" data-testid="billing-current-plan">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="text-xs uppercase tracking-widest text-[#4B5773]">Current plan</div>
              <div className="font-display text-2xl text-[#0B1A36] mt-1">
                {currentPlan ? currentPlan.label : "No plan yet"}
              </div>
              <div className="text-sm text-[#4B5773] mt-1">
                {isFounding
                  ? <span data-testid="billing-founding-tag">Free for 3 months · No card required</span>
                  : currentPlan?.price_gbp != null
                    ? <span>{gbp(currentPlan.price_gbp)} / month</span>
                    : <span>Pick a plan to get started</span>}
              </div>
            </div>
            <span className={`inline-flex items-center gap-1.5 rounded-full text-xs font-semibold px-2.5 py-1 ${TONE_PILL[status.tone]}`}
                  data-testid="billing-status-pill">
              <StatusIcon className="h-3.5 w-3.5"/>{status.label}
            </span>
          </div>
          <div className="grid sm:grid-cols-3 gap-4 mt-6 text-sm">
            <div>
              <div className="text-xs uppercase tracking-widest text-[#4B5773]">Trial ends</div>
              <div className="text-[#0B1A36] mt-0.5">{formatDate(me.trial_end_date)}</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-[#4B5773]">
                {me.cancel_at_period_end ? "Access ends" : "Renews on"}
              </div>
              <div className="text-[#0B1A36] mt-0.5" data-testid="billing-renews-on">
                {formatDate(me.current_period_end)}
              </div>
              {me.cancel_at_period_end && (
                <div className="text-[10px] uppercase tracking-widest text-[#B45309] mt-0.5"
                     data-testid="billing-cancel-flag">
                  Cancellation scheduled
                </div>
              )}
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-[#4B5773]">Last payment</div>
              <div className="text-[#0B1A36] mt-0.5 capitalize">
                {me.last_payment_status ? `${me.last_payment_status} · ${formatDate(me.last_payment_date)}` : "-"}
              </div>
            </div>
          </div>
          <div className="mt-6 flex flex-wrap gap-2">
            {me.has_stripe_subscription && (
              <button data-testid="billing-portal-btn" onClick={openPortal} disabled={busy === "portal"}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-[#0B1A36] text-white text-sm font-medium hover:bg-[#1F2A44] disabled:opacity-60">
                {busy === "portal" ? <Loader2 className="h-4 w-4 animate-spin"/> : <CreditCard className="h-4 w-4"/>}
                Manage billing
              </button>
            )}
            {!me.has_stripe_subscription && !isFounding && (
              <span className="text-xs text-[#4B5773]">Pick a plan below to start your subscription.</span>
            )}
          </div>
        </div>

        {/* Founding programme card */}
        <div className="rounded-xl bg-[#0B1A36] text-white p-6" data-testid="billing-founding-card">
          <div className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-2.5 py-1 text-xs font-semibold">
            <Sparkles className="h-3.5 w-3.5"/> Founding Programme
          </div>
          {founding && (
            <div className="mt-4">
              <div className="font-display text-3xl">
                {founding.claimed}<span className="text-base font-normal opacity-70"> / {founding.limit}</span>
              </div>
              <div className="text-xs uppercase tracking-widest opacity-70 mt-1">places claimed</div>
              <div className="text-sm opacity-90 mt-3" data-testid="billing-founding-status">
                {founding.closed
                  ? "Programme closed."
                  : `${founding.slots_left} slot${founding.slots_left === 1 ? "" : "s"} left until ${formatDate(founding.expiry_date)}`}
              </div>
              {isFounding && me.founding_member_number && (
                <div className="text-xs opacity-70 mt-3">
                  You are member <span className="font-semibold text-white">#{me.founding_member_number}</span>.
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Plans grid */}
      <div>
        <div className="font-display text-xl text-[#0B1A36] mb-4">Choose a plan</div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {plans.filter(p => p.slug !== "founding").map(p => {
            const isCurrent = p.slug === me.plan_slug;
            const isEnterprise = p.slug === "enterprise";
            return (
              <div key={p.slug} data-testid={`plan-card-${p.slug}`}
                   className={`rounded-xl border bg-white p-5 flex flex-col ${isCurrent ? "border-[#2563EB] ring-2 ring-[#2563EB]/20" : "border-slate-200"}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <div className="font-display text-lg text-[#0B1A36]">{p.label}</div>
                    <div className="text-sm text-[#4B5773] mt-0.5">
                      {p.max_employees ? `Up to ${p.max_employees} employees` : "Custom contract"}
                    </div>
                  </div>
                  {isCurrent && <span className="text-[10px] uppercase tracking-widest text-[#1D4ED8] bg-[#DBE7FE] rounded-full px-2 py-0.5">Current</span>}
                </div>
                <div className="font-display text-3xl text-[#0B1A36] mt-4">
                  {isEnterprise ? "Contact us" : <>{gbp(p.price_gbp)}<span className="text-sm font-normal text-[#4B5773]"> /mo</span></>}
                </div>
                <p className="text-xs text-[#4B5773] mt-2 flex-1">{p.description}</p>
                {isEnterprise ? (
                  <a href="mailto:hello@nextrota.com" data-testid="plan-cta-enterprise"
                     className="mt-5 inline-flex items-center justify-center gap-2 px-4 py-2 rounded-md border border-slate-200 text-sm font-medium text-[#0B1A36] hover:bg-slate-50">
                    Contact us <ExternalLink className="h-3.5 w-3.5"/>
                  </a>
                ) : isCurrent && me.has_stripe_subscription ? (
                  <button data-testid={`plan-cta-${p.slug}`} onClick={openPortal} disabled={busy === "portal"}
                    className="mt-5 inline-flex items-center justify-center gap-2 px-4 py-2 rounded-md border border-slate-200 text-sm font-medium text-[#0B1A36] hover:bg-slate-50 disabled:opacity-60">
                    {busy === "portal" ? <Loader2 className="h-3.5 w-3.5 animate-spin"/> : <CreditCard className="h-3.5 w-3.5"/>}
                    Manage
                  </button>
                ) : (
                  <button data-testid={`plan-cta-${p.slug}`} onClick={() => startCheckout(p.slug)} disabled={busy === p.slug}
                    className="mt-5 inline-flex items-center justify-center gap-2 px-4 py-2 rounded-md bg-[#2563EB] text-white text-sm font-medium hover:bg-[#1D4ED8] disabled:opacity-60">
                    {busy === p.slug ? <Loader2 className="h-3.5 w-3.5 animate-spin"/> : <CheckCircle2 className="h-3.5 w-3.5"/>}
                    {me.has_stripe_subscription ? "Switch to this plan" : `Subscribe to ${p.label}`}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Invoices */}
      <div className="rounded-xl border border-slate-200 bg-white" data-testid="billing-invoices">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2">
          <Receipt className="h-4 w-4 text-[#4B5773]"/>
          <div className="font-display text-lg text-[#0B1A36]">Recent invoices</div>
        </div>
        {invoices.length === 0 ? (
          <div className="px-5 py-8 text-sm text-[#4B5773]" data-testid="billing-invoices-empty">
            {me.has_stripe_subscription
              ? "No invoices yet."
              : "Subscribe to a plan to start receiving invoices."}
          </div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {invoices.map(inv => (
              <li key={inv.id} className="px-5 py-3 flex items-center justify-between gap-4 text-sm">
                <div className="min-w-0">
                  <div className="font-medium text-[#0B1A36] truncate">{inv.number || inv.id}</div>
                  <div className="text-xs text-[#4B5773]">{formatDate(new Date(inv.created * 1000).toISOString())} · {inv.status}</div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className="font-mono text-[#0B1A36]">{gbp(inv.amount_paid)}</span>
                  {inv.hosted_invoice_url && (
                    <a href={inv.hosted_invoice_url} target="_blank" rel="noreferrer"
                       data-testid={`billing-invoice-${inv.id}`}
                       className="text-[#2563EB] hover:underline inline-flex items-center gap-1 text-xs">
                      View <ExternalLink className="h-3 w-3"/>
                    </a>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}


// ---------- R5.a (Feb 2026) Trial-size section ---------------------
//
// Visible only when `isActiveTrial` on BillingPage. Three tiles map
// 1:1 to the R5.a approved endpoint `POST /api/billing/trial-plan`.
// Uses the existing /billing/me snapshot for active_employees and the
// current plan_slug; posts the new slug and re-loads. No new endpoint
// beyond the one approved. Command tile shows "Command" and submits
// the internal slug `business`.

function TrialSizeSection({ me, onChanged }) {
  const [saving, setSaving] = useState(null);  // slug being saved
  const activeEmployees = me?.active_employees ?? 0;
  const currentSlug = me?.plan_slug || "starter";
  const OPTIONS = [
    { slug: "starter",  label: "Starter trial", max: 10,
      body: "Starter trial: up to 10 active employees" },
    { slug: "growth",   label: "Growth trial",  max: 30,
      body: "Growth trial: up to 30 active employees" },
    { slug: "business", label: "Command trial", max: 75,
      body: "Command trial: up to 75 active employees" },
  ];

  async function change(slug) {
    if (slug === currentSlug) return;
    setSaving(slug);
    try {
      await api.post("/billing/trial-plan", { plan_slug: slug });
      toast.success("Trial size updated");
      await onChanged?.();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail?.code === "PLAN_TOO_SMALL_FOR_EMPLOYEES") {
        toast.error(detail.message);
      } else {
        toast.error(formatError(detail) || "Could not change trial size.");
      }
    } finally {
      setSaving(null);
    }
  }

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-6"
         data-testid="billing-trial-size-section">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#4B5773]">
            Trial size
          </div>
          <h2 className="font-display text-xl text-[#0B1A36] mt-1"
              data-testid="billing-trial-size-title">
            Change trial size
          </h2>
          <p className="text-sm text-[#4B5773] mt-1 max-w-2xl">
            Your trial includes full Command access. Choose the employee
            limit that fits your team during the trial.
          </p>
        </div>
        <span className="text-[11px] text-[#4B5773] shrink-0 self-start whitespace-nowrap">
          {activeEmployees} active employee{activeEmployees === 1 ? "" : "s"} today
        </span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2"
           data-testid="billing-trial-size-tiles">
        {OPTIONS.map((opt) => {
          const active = currentSlug === opt.slug;
          const tooSmall = activeEmployees > opt.max;
          const isSaving = saving === opt.slug;
          return (
            <button
              key={opt.slug}
              type="button"
              disabled={active || tooSmall || !!saving}
              onClick={() => change(opt.slug)}
              data-testid={`billing-trial-size-tile-${opt.slug}`}
              className={[
                "text-left rounded-lg border px-4 py-3 transition-colors",
                active
                  ? "border-[#2563EB] bg-[#EFF3FB]"
                  : tooSmall
                    ? "border-slate-200 bg-slate-50 opacity-60 cursor-not-allowed"
                    : "border-slate-200 bg-white hover:border-[#2563EB]/40",
              ].join(" ")}
            >
              <p className="text-[13px] font-semibold text-[#0B1A36]">
                {opt.label}{active ? " (current)" : ""}
              </p>
              <p className="text-[11px] text-[#4B5773] mt-1">
                {opt.body}
              </p>
              {tooSmall && (
                <p className="text-[11px] text-amber-700 mt-1"
                   data-testid={`billing-trial-size-tile-${opt.slug}-too-small`}>
                  Your team is too large for this trial size. Deactivate
                  employees first.
                </p>
              )}
              {isSaving && (
                <span className="inline-flex items-center gap-1 text-[11px] text-[#2563EB] mt-1">
                  <Loader2 className="h-3 w-3 animate-spin" /> Saving...
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

